/** Чернетка налаштувань подання ↔ збережене подання API: створення, тіло запису, стовпці (вибір і порядок). Чисті функції. */
import type { IFieldDefinition, IRecordTypeDefinition, ISavedView, ISavedViewWrite, PageSize, SavedViewKind } from '@platform/core/types';
import { cardRulesOf } from '../../views/cards/cardRules';
import { conditionsToDsl, dslToConditions } from '../advanced/advancedFilter';
import type { IViewDefinition } from './IViewDefinition';
import type { IViewDraft } from './IViewDraft';

const SIZES: readonly number[] = [10, 15, 25, 50, 100];

/** Поля карток нового подання (прототип v4 DEFCARD): статус, пріоритет, виконавець, термін, теги — ті, що є в типі. */
const DEFAULT_CARD: readonly string[] = ['status', 'priority', 'assignee', 'due', 'tags'];

/** Чернетка з подання (редагування) або з поточного стану списку (нове подання з назвою `name`; поля карток — типові поля типу `rt`). */
export function draftOf(
  view: ISavedView | undefined, base: { kind: SavedViewKind; columns: string[]; sort: string; pageSize: PageSize }, name: string, rt?: IRecordTypeDefinition,
): IViewDraft {
  const cardFields = DEFAULT_CARD.filter((k) => !rt || rt.fields.some((f) => f.key === k && !f.hidden));
  if (!view) return { name, kind: base.kind, columns: base.columns, groupBy: '', cardFields, rules: [], filters: [], sort: base.sort, pageSize: base.pageSize };
  const d = view.definition;
  const pageSize = d.pageSize && SIZES.includes(d.pageSize) ? (d.pageSize as PageSize) : base.pageSize;
  return {
    name: view.name, kind: view.kind, columns: d.columns?.length ? d.columns : base.columns, groupBy: d.groupBy ?? '', cardFields: d.cardFields ?? cardFields,
    rules: cardRulesOf(d), filters: dslToConditions(d.filter ?? ''), sort: d.sort ?? '', pageSize,
  };
}

/** Тіло збереження: порожня назва — `untitled`; фільтр — лише повні рядки умов; поля й правила карток — для видів, що мають картки. */
export function writeOf(draft: IViewDraft, rtKey: string, untitled: string): ISavedViewWrite {
  const filter = conditionsToDsl(draft.filters);
  const cards = draft.kind !== 'table';
  const definition: IViewDefinition = {
    columns: draft.columns, pageSize: draft.pageSize, ...(filter ? { filter } : {}), ...(draft.sort ? { sort: draft.sort } : {}),
    ...(draft.groupBy && (draft.kind === 'any' || draft.kind === 'board') ? { groupBy: draft.groupBy } : {}),
    ...(cards ? { cardFields: draft.cardFields, cardRules: draft.rules.filter((r) => r.field) } : {}),
  };
  return { recordTypeKey: rtKey, name: draft.name.trim() || untitled, kind: draft.kind, definition };
}

/** Вмикає або вимикає стовпець (увімкнений додається в кінець). */
export function toggleColumn(columns: string[], key: string): string[] {
  return columns.includes(key) ? columns.filter((c) => c !== key) : [...columns, key];
}

/** Переміщує вибраний стовпець на одну позицію вгору (−1) чи вниз (+1); на краю — без змін. */
export function moveColumn(columns: string[], key: string, delta: -1 | 1): string[] {
  const i = columns.indexOf(key);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= columns.length) return columns;
  const next = [...columns];
  [next[i], next[j]] = [next[j]!, next[i]!];
  return next;
}

/**
 * Поля для списку стовпців: спершу вибрані в порядку показу, далі решта в порядку типу (без довгого тексту, JSON і назви — назва
 * в таблиці показується завжди окремим стовпцем).
 */
export function orderedFields(rt: IRecordTypeDefinition, columns: string[]): IFieldDefinition[] {
  const fields = rt.fields.filter((f) => f.type !== 'textarea' && f.type !== 'json' && f.key !== 'title' && !f.hidden);
  const chosen = columns.map((k) => fields.find((f) => f.key === k)).filter((f): f is IFieldDefinition => !!f);
  return [...chosen, ...fields.filter((f) => !columns.includes(f.key))];
}
